import { useState } from 'react';
import { allGenres, allRegions, allYears } from '../../../mocks/movies';

export interface CategoryFilterState {
  region: string;
  year: number | null;
  genre: string;
  minRating: number;
}

interface FilterBarProps {
  value: CategoryFilterState;
  onChange: (next: CategoryFilterState) => void;
  hideGenreFilter?: boolean;
  onReset: () => void;
}

const RATING_OPTIONS = [
  { label: '全部评分', value: 0 },
  { label: '8分以上', value: 8 },
  { label: '7分以上', value: 7 },
  { label: '6分以上', value: 6 },
];

function FilterRow({
  label,
  options,
  activeValue,
  onSelect,
}: {
  label: string;
  options: { label: string; value: string | number | null }[];
  activeValue: string | number | null;
  onSelect: (value: string | number | null) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm text-foreground-500 sm:w-16 shrink-0 pt-1.5">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isActive = option.value === activeValue;
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`px-3.5 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                isActive
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({ value, onChange, hideGenreFilter = false, onReset }: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);

  const update = (partial: Partial<CategoryFilterState>) => onChange({ ...value, ...partial });

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
      <div className="flex items-center justify-between mb-2 lg:mb-0">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground-700">
          <i className="ri-filter-3-line text-lg text-primary-500" aria-hidden="true"></i>
          筛选条件
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-foreground-500 hover:text-primary-600 whitespace-nowrap cursor-pointer"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="lg:hidden flex items-center gap-1 text-xs text-primary-600 whitespace-nowrap cursor-pointer"
          >
            {expanded ? '收起' : '展开'}
            <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className={`${expanded ? 'block' : 'hidden'} lg:block mt-2`}>
        <FilterRow
          label="地区"
          activeValue={value.region}
          onSelect={(region) => update({ region: (region as string) ?? '全部' })}
          options={[{ label: '全部', value: '全部' }, ...allRegions.map((region) => ({ label: region, value: region }))]}
        />
        <FilterRow
          label="年份"
          activeValue={value.year}
          onSelect={(year) => update({ year: year as number | null })}
          options={[{ label: '全部', value: null }, ...allYears.map((year) => ({ label: `${year}`, value: year }))]}
        />
        {!hideGenreFilter && (
          <FilterRow
            label="类型"
            activeValue={value.genre}
            onSelect={(genre) => update({ genre: (genre as string) ?? '全部' })}
            options={[{ label: '全部', value: '全部' }, ...allGenres.map((genre) => ({ label: genre, value: genre }))]}
          />
        )}
        <FilterRow
          label="评分"
          activeValue={value.minRating}
          onSelect={(rating) => update({ minRating: (rating as number) ?? 0 })}
          options={RATING_OPTIONS}
        />
      </div>
    </div>
  );
}